import React from 'react';
import { Alert, Popconfirm } from 'antd';
import { isEmpty } from 'lodash';
import dayjs from 'dayjs';
import './style.less';
import { Tooltip } from 'antd';

const JourneyInfoDisplay = ({ type, journeyInfo, systemTime, showChangeAlert = true, onCloseAlert, onTicketStatusChange, onEditTransport, onEditCar, onViewTransportHistory }) => {
    // 获取系统时间标签
    const timeLabel = type === 'arrival' ? '已接受日程最早开始时间' : '已接受日程最晚结束时间';

    // 解析 journeyInfo 结构
    const transportInfo = journeyInfo?.transport || { need: false };
    const carInfo = journeyInfo?.car || { need: false };

    // 根据need字段获取标签文本
    const getNeedLabel = needValue => {
        if (needValue === 1) return '（会务组安排）';
        if (needValue === 2) return '（自费）';
        return '';
    };

    // 判断need字段是否为空（undefined、null或false）
    const isNeedEmpty = needValue => {
        return needValue === undefined || needValue === null || needValue === false;
    };

    // 判断交通信息是否有实质性内容
    const hasTransportContent = info => {
        if (!info || !info.transport_list || info.transport_list.length === 0) {
            return false;
        }
        // 检查是否至少有一条交通信息有任何字段填写
        return info.transport_list.some(transport => transport.transport_type || transport.transport_no || transport.seat_type || transport.departure_datetime || transport.departure_place || transport.arrival_datetime || transport.arrival_place);
    };

    // 判断用车信息是否有实质性内容
    const hasCarContent = info => {
        if (!info || !info.car_info) {
            return false;
        }
        const carInfo = info.car_info;
        // 检查是否有任何字段填写
        return !!(carInfo.car_type_demand || carInfo.departure_datetime || carInfo.departure_place || carInfo.arrival_place || carInfo.car_type || carInfo.car_no || carInfo.driver_name || carInfo.driver_phone);
    };

    return (
        <div className="journey-info-display">
            {/* 系统提示 - 始终显示（如果有已确认任务） */}
            {systemTime && (
                <div className="earliest-task-time">
                    <span className="system-tag">系统</span>
                    <span className="time-text">
                        {timeLabel} {systemTime}
                    </span>
                </div>
            )}

            {/* 交通信息或无需交通 */}
            {transportInfo.need === 0 ? (
                <div className="info-section transport-section no-need-single">
                    <div className="section-content">
                        <div className="no-need-content">
                            <i className="bi bi-slash-circle"></i>
                            <span>无需交通</span>
                        </div>
                        {/* 备注信息（无需交通时也展示，置于“无需交通”下方，单行） */}
                        {transportInfo.remarks && (
                            <div className="remark-alert-wrapper">
                                <Alert title={transportInfo.remarks} type="warning" showIcon={false} className="remark-alert" />
                            </div>
                        )}
                        <div className="time-and-actions">
                            {/* 最后更新时间（无需交通时也展示） */}
                            {transportInfo.updated_at && (
                                <div className="last-update-time">
                                    <i className="bi bi-clock"></i>
                                    <span>最后更新时间 {transportInfo.updated_at}</span>
                                </div>
                            )}

                            {/* 操作按钮 */}
                            <div className="journey-actions">
                                <div className="action-item" onClick={onViewTransportHistory}>
                                    <i className="bi bi-clock-history"></i>
                                    <span>变更记录</span>
                                </div>
                                <div className="action-item" onClick={onEditTransport}>
                                    <i className="bi bi-pencil"></i>
                                    <span>编辑</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            ) : !isNeedEmpty(transportInfo.need) && hasTransportContent(transportInfo) ? (
                <div className="info-section transport-section">
                    <div className="section-header">交通{getNeedLabel(transportInfo.need)}</div>
                    <div className="section-content">
                        {/* 交通列表 */}
                        {transportInfo.transport_list.map((transport, index) => {
                            // 根据交通类型确定图标和文本
                            const transportTypeText = transport.transport_type === 1 ? '飞机' : '火车';
                            const transportIcon = transport.transport_type === 1 ? 'airplane' : 'train-front';
                            const isIssued = transport.ticket_ready === true;

                            // 判断是否有时间信息
                            const hasDepartureTime = transport.departure_datetime && transport.departure_datetime.trim() !== '';
                            const hasArrivalTime = transport.arrival_datetime && transport.arrival_datetime.trim() !== '';

                            let timeDisplay = '';
                            if (hasDepartureTime && hasArrivalTime) {
                                // 判断是否同一天
                                const departureDate = transport.departure_datetime.substring(0, 10);
                                const arrivalDate = transport.arrival_datetime.substring(0, 10);
                                const isSameDay = departureDate === arrivalDate;

                                // 根据是否同一天决定显示格式
                                timeDisplay = isSameDay ? `${departureDate} ${transport.departure_datetime.substring(11, 16)}～${transport.arrival_datetime.substring(11, 16)}` : `${departureDate} ${transport.departure_datetime.substring(11, 16)}～${arrivalDate} ${transport.arrival_datetime.substring(11, 16)}`;
                            } else if (hasDepartureTime) {
                                // 只有出发时间
                                const departureDate = transport.departure_datetime.substring(0, 10);
                                timeDisplay = `${departureDate} ${transport.departure_datetime.substring(11, 16)}`;
                            } else if (hasArrivalTime) {
                                // 只有抵达时间
                                const arrivalDate = transport.arrival_datetime.substring(0, 10);
                                timeDisplay = `${arrivalDate} ${transport.arrival_datetime.substring(11, 16)}`;
                            }

                            // 判断是否有地点信息
                            const hasDeparturePlace = transport.departure_place && transport.departure_place.trim() !== '';
                            const hasArrivalPlace = transport.arrival_place && transport.arrival_place.trim() !== '';

                            let placeDisplay = '';
                            if (hasDeparturePlace && hasArrivalPlace) {
                                // 同时有出发地和抵达地：显示箭头连接
                                placeDisplay = `${transport.departure_place}→${transport.arrival_place}`;
                            } else if (hasDeparturePlace) {
                                // 只有出发地
                                placeDisplay = transport.departure_place;
                            } else if (hasArrivalPlace) {
                                // 只有抵达地
                                placeDisplay = transport.arrival_place;
                            }

                            return (
                                <div key={index} className="transport-item">
                                    <i className={`bi bi-${transportIcon} transport-icon`}></i>
                                    <div className="transport-details">
                                        <span className="transport-text">
                                            {transportTypeText} {transport.transport_no} {transport.seat_type} {placeDisplay} {timeDisplay}
                                            <span className={`ticket-status-text ${isIssued ? 'issued' : 'not-issued'}`}>{isIssued ? '已出票' : '未出票'}</span>
                                        </span>
                                        <Popconfirm title="确认标记已出票？" onConfirm={() => onTicketStatusChange && onTicketStatusChange(index, isIssued ? false : true)} okText="确认" cancelText="取消">
                                            <Tooltip title={isIssued ? '标记为未出票' : '标记为已出票'}>
                                                <i className={`bi bi-check-circle-fill ticket-status-icon ${isIssued ? 'active' : ''}`}></i>
                                            </Tooltip>
                                        </Popconfirm>
                                    </div>
                                </div>
                            );
                        })}

                        {/* 备注信息 */}
                        {transportInfo.remarks && (
                            <div className="remark-alert-wrapper">
                                <Alert title={transportInfo.remarks} type="warning" showIcon={false} className="remark-alert" />
                            </div>
                        )}

                        <div className="time-and-actions">
                            {/* 最后更新时间 */}
                            {transportInfo.updated_at && (
                                <div className="last-update-time">
                                    <i className="bi bi-clock"></i>
                                    <span>最后更新时间 {transportInfo.updated_at}</span>
                                </div>
                            )}

                            {/* 操作按钮 */}
                            <div className="journey-actions">
                                <div className="action-item" onClick={onViewTransportHistory}>
                                    <i className="bi bi-clock-history"></i>
                                    <span>变更记录</span>
                                </div>
                                {/* 只有在交通信息存在时才展示"变更"文案的按钮 */}
                                <div className="action-item" onClick={onEditTransport}>
                                    <i className="bi bi-pencil"></i>
                                    <span>变更</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            ) : (
                // 交通信息为空时也显示编辑按钮
                <div className="info-section transport-section empty">
                    <div className="section-header">{isNeedEmpty(transportInfo.need) ? '交通' : `交通${getNeedLabel(transportInfo.need)}`}</div>
                    <div className="section-content">
                        <div className="empty-block">
                            <div className="empty-content">暂未填写</div>
                            {/* 备注信息（空状态也展示，置于“暂未填写”下方，Alert样式，与内容态一致） */}
                            {transportInfo.remarks && (
                                <div className="remark-alert-wrapper">
                                    <Alert title={transportInfo.remarks} type="warning" showIcon={false} className="remark-alert" />
                                </div>
                            )}
                            <div className="time-and-actions">
                                {/* 最后更新时间（空状态也展示） */}
                                {transportInfo.updated_at && (
                                    <div className="last-update-time">
                                        <i className="bi bi-clock"></i>
                                        <span>最后更新时间 {transportInfo.updated_at}</span>
                                    </div>
                                )}
                                {/* 编辑按钮 */}
                                <div className="journey-actions">
                                    <div className="action-item" onClick={onEditTransport}>
                                        <i className="bi bi-pencil"></i>
                                        <span>编辑</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* 用车信息或无需用车 */}
            {carInfo.need === 0 ? (
                <div className="info-section vehicle-section no-need-single">
                    <div className="section-content">
                        <div className="no-need-content">
                            <i className="bi bi-slash-circle"></i>
                            <span>无需用车</span>
                        </div>
                        {/* 备注信息（无需用车时也展示，置于“无需用车”下方，单行） */}
                        {carInfo.remarks && (
                            <div className="remark-alert-wrapper">
                                <Alert title={carInfo.remarks} type="warning" showIcon={false} className="remark-alert" />
                            </div>
                        )}
                        <div className="time-and-actions">
                            {/* 最后更新时间（无需用车时也展示） */}
                            {carInfo.updated_at && (
                                <div className="last-update-time">
                                    <i className="bi bi-clock"></i>
                                    <span>最后更新时间 {carInfo.updated_at}</span>
                                </div>
                            )}
                            {/* 编辑按钮 */}
                            <div className="journey-actions">
                                <div className="action-item" onClick={() => onEditCar && onEditCar(type)}>
                                    <i className="bi bi-pencil"></i>
                                    <span>编辑</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            ) : !isNeedEmpty(carInfo.need) && hasCarContent(carInfo) ? (
                <div className="info-section vehicle-section">
                    <div className="section-header">用车{getNeedLabel(carInfo.need)}</div>
                    <div className="section-content">
                        <div className="vehicle-item">
                            <i className="bi bi-car-front vehicle-icon"></i>
                            <div className="vehicle-details">
                                {/* 用车需求：车型需求 + 出发地点 至 抵达地点 + 日期 + 出发时间 */}
                                {(carInfo.car_info.car_type_demand || carInfo.car_info.departure_place || carInfo.car_info.arrival_place || carInfo.car_info.departure_datetime) && (
                                    <div className="vehicle-text">
                                        用车需求：
                                        {carInfo.car_info.car_type_demand || ''}
                                        {carInfo.car_info.departure_place && carInfo.car_info.arrival_place && (
                                            <>
                                                {' '}
                                                {carInfo.car_info.departure_place}至{carInfo.car_info.arrival_place}
                                            </>
                                        )}
                                        {carInfo.car_info.departure_datetime && <> {dayjs(carInfo.car_info.departure_datetime).format('MM月DD日 HH:mm')}出发</>}
                                    </div>
                                )}
                                {/* 用车安排：司机姓名 + 司机电话 + 车型 + 颜色 + 车牌号 */}
                                {(carInfo.car_info.driver_name || carInfo.car_info.driver_phone || carInfo.car_info.car_type || carInfo.car_info.car_no) && (
                                    <div className="vehicle-text">
                                        用车安排：
                                        {carInfo.car_info.driver_name || ''}
                                        {carInfo.car_info.driver_phone && <> {carInfo.car_info.driver_phone}</>}
                                        {carInfo.car_info.car_type && <> {carInfo.car_info.car_type}</>}
                                        {carInfo.car_info.car_no && <>{carInfo.car_info.car_no}</>}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* 备注信息 */}
                        {carInfo.remarks && (
                            <div className="remark-alert-wrapper">
                                <Alert title={carInfo.remarks} type="warning" showIcon={false} className="remark-alert" />
                            </div>
                        )}

                        <div className="time-and-actions">
                            {/* 最后更新时间 */}
                            {carInfo.updated_at && (
                                <div className="last-update-time">
                                    <i className="bi bi-clock"></i>
                                    <span>最后更新时间 {carInfo.updated_at}</span>
                                </div>
                            )}

                            {/* 操作按钮 */}
                            <div className="journey-actions">
                                <div className="action-item" onClick={() => onEditCar && onEditCar(type)}>
                                    <i className="bi bi-pencil"></i>
                                    <span>编辑</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            ) : (
                // 用车信息为空时也显示编辑按钮
                <div className="info-section vehicle-section empty">
                    <div className="section-header">{isNeedEmpty(carInfo.need) ? '用车' : `用车${getNeedLabel(carInfo.need)}`}</div>
                    <div className="section-content">
                        <div className="empty-block">
                            <div className="empty-content">暂未填写</div>
                            {/* 备注信息（空状态也展示，置于“暂未填写”下方，Alert样式，与内容态一致） */}
                            {carInfo.remarks && (
                                <div className="remark-alert-wrapper">
                                    <Alert title={carInfo.remarks} type="warning" showIcon={false} className="remark-alert" />
                                </div>
                            )}
                            <div className="time-and-actions">
                                {/* 最后更新时间（空状态也展示） */}
                                {carInfo.updated_at && (
                                    <div className="last-update-time">
                                        <i className="bi bi-clock"></i>
                                        <span>最后更新时间 {carInfo.updated_at}</span>
                                    </div>
                                )}
                                {/* 编辑按钮 */}
                                <div className="journey-actions">
                                    <div className="action-item" onClick={() => onEditCar && onEditCar(type)}>
                                        <i className="bi bi-pencil"></i>
                                        <span>编辑</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default JourneyInfoDisplay;
